// Iter37 — Incidents Kanban view (B13).
//
// Read-only 5-column board (S1, S2, S3, S4, Precaution). Reuses the Breeze
// card styling for visual consistency, but is a separate component because:
//   * it operates on filtered incident lists (existing IncidentFilters apply)
//   * cards include tenant name + ID prefix
//   * mobile: horizontal scroll across columns (Breeze uses a 2-up grid)
//   * no header / summary — that's the Incidents-page chrome.
//
// AI Watch indicator rule (no API): SLA remaining < 20% AND severity !== "s1"
// → amber dashed outline + "⚠ Watch" badge.
import React, { useMemo } from "react";
import { severityColor } from "../data/mockData";
import { useT } from "../lib/i18n";
import { TranslatableText } from "./TranslatableText";

const SEVERITIES = ["s1", "s2", "s3", "s4", "precaution"];
const SEV_BADGE = { s1: "S1", s2: "S2", s3: "S3", s4: "S4", precaution: "PRE" };
const MAX_CARD_TITLE = 70;

const truncate = (s, n) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || "");

const slaBarColor = (pct) => {
  if (pct >= 100) return "#E24B4A";
  if (pct >= 75) return "#f3b14a";
  return "#4ed084";
};

const statusBadgeColors = (status) => {
  const map = {
    open: { bg: "rgba(24,95,165,0.18)", color: "#9ec3ec" },
    acknowledged: { bg: "rgba(78,208,132,0.14)", color: "#4ed084" },
    "in-progress": { bg: "rgba(243,177,74,0.18)", color: "#f3b14a" },
    in_progress: { bg: "rgba(243,177,74,0.18)", color: "#f3b14a" },
    escalated: { bg: "rgba(226,75,74,0.18)", color: "#ff9a99" },
    contained: { bg: "rgba(78,208,132,0.18)", color: "#4ed084" },
    resolved: { bg: "rgba(78,208,132,0.18)", color: "#4ed084" },
    closed: { bg: "rgba(120,125,140,0.18)", color: "#9aa1b3" },
  };
  return map[(status || "open").toLowerCase()] || map.open;
};

const KanbanCard = ({ inc, onClick }) => {
  const { t } = useT();
  const sev = severityColor(inc.severity);
  const sla = Number.isFinite(inc.sla) ? inc.sla : 0;
  const breached = sla > 100;
  // SLA remaining < 20% (i.e., sla >= 80%) AND not S1 AND not breached.
  const watchSoon = sla >= 80 && !breached && inc.severity !== "s1";
  const bar = Math.min(100, Math.max(0, sla));
  const sb = statusBadgeColors(inc.status);
  const tenant = inc.tenant || (inc.parties ? String(inc.parties).split(/[—↔→·,]/)[0].trim() : "");
  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded transition-colors"
      style={{
        background: "var(--bg3, #0d0f18)",
        border: `1px solid ${breached ? "#E24B4A" : "rgba(255,255,255,0.08)"}`,
        borderLeft: `3px solid ${sev}`,
        borderRadius: 4,
        padding: "8px 10px",
        cursor: "pointer",
        position: "relative",
        animation: breached ? "breeze-card-pulse-red 1.6s ease-in-out infinite" : undefined,
        outline: watchSoon ? "1px dashed #f3b14a" : "none",
        outlineOffset: watchSoon ? -2 : 0,
        minHeight: 92,
      }}
      data-testid={`kanban-card-${inc.id}`}
      data-severity={inc.severity}
      data-sla-breach={breached ? "true" : "false"}
      data-watch={watchSoon ? "true" : "false"}
    >
      <div className="flex items-center gap-1.5">
        <span className="inline-block rounded-full" style={{ width: 6, height: 6, background: sev }} />
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9, color: "#9ec3ec" }}>{inc.id}</span>
        {watchSoon && (
          <span
            className="ml-auto"
            style={{
              background: "rgba(243,177,74,0.18)",
              color: "#f3b14a",
              fontFamily: "'DM Mono', monospace",
              fontSize: 8,
              padding: "1px 4px",
              borderRadius: 2,
              letterSpacing: "0.4px",
              fontWeight: 700,
            }}
            data-testid="kanban-card-watch-badge"
          >
            {t("incidents.kanban_watch")}
          </span>
        )}
      </div>
      <div
        className="mt-1"
        style={{
          fontFamily: "Syne, sans-serif",
          fontWeight: 600,
          fontSize: 11,
          color: "var(--text, #f0f2f8)",
          lineHeight: 1.3,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        <TranslatableText
          text={truncate(inc.title, MAX_CARD_TITLE)}
          testId={`kanban-card-title-${inc.id}`}
          skipBadge
        />
      </div>
      {tenant && (
        <div
          className="mt-0.5"
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            color: "#9198b0",
            letterSpacing: "0.3px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          data-testid={`kanban-card-tenant-${inc.id}`}
        >
          {tenant}
        </div>
      )}
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 9,
            color: "#9198b0",
            letterSpacing: "0.4px",
          }}
        >
          {bar.toFixed(0)}%
        </span>
        <span
          className="inline-block px-1 py-0.5 rounded"
          style={{
            background: sb.bg,
            color: sb.color,
            fontFamily: "'DM Mono', monospace",
            fontSize: 8,
            fontWeight: 700,
            letterSpacing: "0.4px",
          }}
          data-testid={`kanban-card-status-${inc.id}`}
        >
          {t(`status.${(inc.status || "open").toLowerCase()}`)}
        </span>
      </div>
      <div
        className="mt-1.5 rounded-full"
        style={{ height: 3, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}
        data-testid={`kanban-card-sla-bar-${inc.id}`}
      >
        <div
          style={{
            height: "100%",
            width: `${bar}%`,
            background: slaBarColor(sla),
            transition: "width 200ms ease-out",
          }}
        />
      </div>
    </button>
  );
};

const KanbanColumn = ({ sev, items }) => {
  const { t } = useT();
  const color = severityColor(sev);
  return (
    <div
      className="flex flex-col rounded-md flex-shrink-0"
      style={{
        width: "min(280px, 86vw)",
        minWidth: 240,
        maxHeight: 720,
        background: "var(--bg2, #0a0c12)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
      data-testid={`kanban-column-${sev}`}
    >
      <div
        className="flex items-center gap-2 px-3 py-2"
        style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        data-testid={`kanban-column-header-${sev}`}
      >
        <span
          style={{
            background: `${color}22`,
            color,
            fontFamily: "'DM Mono', monospace",
            fontSize: 10,
            padding: "2px 6px",
            borderRadius: 3,
            fontWeight: 700,
            letterSpacing: "0.5px",
          }}
        >
          {SEV_BADGE[sev]}
        </span>
        <span
          style={{ fontFamily: "Syne, sans-serif", fontSize: 11, color: "var(--text, #f0f2f8)", fontWeight: 600 }}
          data-testid={`kanban-column-label-${sev}`}
        >
          {t(`severity.${sev}`)}
        </span>
        <span
          className="ml-auto"
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
            color: "var(--text, #f0f2f8)",
            fontWeight: 600,
          }}
          data-testid={`kanban-column-count-${sev}`}
        >
          {items.length}
        </span>
      </div>
      <div
        className="flex-1 overflow-y-auto px-2 py-2 space-y-2"
        style={{ scrollbarWidth: "thin", minHeight: 240 }}
        data-testid={`kanban-column-body-${sev}`}
      >
        {items.length === 0 ? (
          <div
            className="text-center py-6"
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              color: "#6b7388",
              letterSpacing: "0.4px",
            }}
            data-testid={`kanban-column-empty-${sev}`}
          >
            {t("incidents.kanban_no_incidents")}
          </div>
        ) : (
          items.map((inc) => <KanbanCard key={inc.id} inc={inc} onClick={inc.__onClick} />)
        )}
      </div>
    </div>
  );
};

export const IncidentsKanban = ({ incidents = [], onCardClick }) => {
  const grouped = useMemo(() => {
    const out = {};
    for (const s of SEVERITIES) out[s] = [];
    for (const inc of incidents) {
      const sev = (inc.severity || "").toLowerCase();
      const key = SEVERITIES.includes(sev)
        ? sev
        : sev === "p" || sev === "pre"
        ? "precaution"
        : null;
      if (!key) continue;
      out[key].push({ ...inc, __onClick: () => onCardClick && onCardClick(inc.id) });
    }
    for (const s of SEVERITIES) {
      out[s].sort((a, b) => {
        const ta = new Date(a.createdAt || a.opened_at || 0).getTime();
        const tb = new Date(b.createdAt || b.opened_at || 0).getTime();
        return ta - tb;
      });
    }
    return out;
  }, [incidents, onCardClick]);

  return (
    <div
      className="overflow-x-auto pb-2"
      style={{ scrollbarWidth: "thin" }}
      data-testid="incidents-kanban"
    >
      <div className="flex gap-3 min-w-min">
        {SEVERITIES.map((sev) => (
          <KanbanColumn key={sev} sev={sev} items={grouped[sev]} />
        ))}
      </div>
    </div>
  );
};
